import { toKebabCase as kebabCase } from "remeda"
import type { Constructor, Simplify } from "type-fest"
import { WithAccessors } from "./with-accessors"

type BindSerializer<
	Base extends Constructor<object>,
	InnerSerializer,
> = BindSerializerHelper<
	InstanceType<Base>,
	InnerSerializer,
	PropsFromSerializer<InnerSerializer>
>

type BindSerializerHelper<This, InnerSerializer, AccessorProps> = {
	[Key in keyof InnerSerializer]: InnerSerializer[Key] extends UnboundSerializer<
		infer GetValue,
		infer SetValue
	>
		? Key extends keyof This
			? AttributeSerializer<
					SerializerThis<This, AccessorProps>,
					GetValue & This[Key],
					SetValue | This[Key]
				>
			: AttributeSerializer<
					SerializerThis<This, AccessorProps>,
					GetValue,
					SetValue
				>
		: InnerSerializer[Key]
}

/**
 * Mixin that adds accessors to a class.
 * @param Base The base class to add accessors to.
 * @param accessors The accessors to add to the class.
 */
export function WithAttributeProps<
	Base extends Constructor<AttributeTarget>,
	// biome-ignore lint/suspicious/noExplicitAny: Any serializer is allowed.
	InnerSerializer extends Record<string, UnconstrainedSerializer<any, any>>,
>(
	Base: Base,
	// We need to bind it again, because this is not going to be
	// preserved if it was not explicitly defined in Serializer.
	// Bounding it again allows this to be properly inferred when accessors is
	// provided inline without needing to explicitly define it.
	serializers: BindSerializer<Base, InnerSerializer>,
) {
	let accessors = createAccessors<InnerSerializer>(
		serializers as InnerSerializer,
	)
	return WithAccessors<Base, typeof accessors>(
		Base,
		// I don't know why `as` is needed but I'm done looking for the answer.
		accessors as Parameters<typeof WithAccessors<Base, typeof accessors>>[1],
	)
}

type AttributeTarget = Pick<
	HTMLElement,
	"getAttribute" | "setAttribute" | "removeAttribute"
>

function createAccessors<
	InnerSerializers extends Record<
		string,
		UnconstrainedSerializer<unknown, unknown>
	>,
>(attributeSerializers: InnerSerializers) {
	let result = {} as Record<string, unknown>
	for (let key in attributeSerializers) {
		let serializer = attributeSerializers[key]
		result[key] = createAccessor(key, serializer)
	}
	return result as AccessorsFromSerializers<InnerSerializers>
}

function createAccessor(
	name: string,
	serializer: UnconstrainedSerializer<unknown, unknown>,
) {
	let attributeName = kebabCase(name)
	const { parse, serialize } = serializer
	if (parse == null || serialize == null) {
		throw new Error("parse and serialize must be defined in the serializer.")
	}
	return {
		get(this: AttributeTarget) {
			let value = this.getAttribute(attributeName)
			return parse.call(this, value)
		},
		set(this: AttributeTarget, value: unknown) {
			let serialized = serialize.call(this, value)
			if (serialized == null) {
				this.removeAttribute(attributeName)
			} else {
				this.setAttribute(attributeName, serialized)
			}
		},
	}
}

type PropsFromSerializer<T> = Simplify<{
	[K in keyof T as T[K] extends AttributeSerializer<
		infer _1,
		infer _2,
		infer _3
	>
		? K
		: never]: T[K] extends AttributeSerializer<
		infer _1,
		infer _2,
		infer SetValue
	>
		? SetValue
		: never
}>

// I am not sure why, but it does seem that at least serialize
// must be made optional here. It may be because of the serialize protection
// there is in the Serializer type.
type UnconstrainedSerializer<GetValue, SetValue> = {
	parse?: (a: string | null) => GetValue
	serialize?: (value: SetValue) => string | null
}

type UnboundSerializer<GetValue, SetValue = GetValue> = {
	parse: (a: string | null) => GetValue
	serialize: (value: SetValue) => string | null
}

// I wish I could use Simplify here, but it seems to be breaking the type of
// this of the attribute serializers parameter....
export type AttributeSerializer<This, GetValue, SetValue = GetValue> = {
	parse: (this: This, a: string | null) => GetValue
	serialize: (this: This, value: SetValue) => string | null
}

type SerializerThis<This, AccessorProps> = Simplify<
	Omit<This, keyof AccessorProps> & AccessorProps
>

type AccessorsFromSerializers<D> = {
	[K in keyof D]: D[K] extends UnconstrainedSerializer<infer P, infer S>
		? {
				get(this: AttributeTarget): P
				set(this: AttributeTarget, value: S): void
			}
		: never
}

export function number(options: {
	default: number
}): AttributeSerializer<unknown, number, number>
export function number(options?: {
	default?: null | undefined
}): AttributeSerializer<unknown, number | null, number | null>
export function number({
	default: defaultValue = null,
}: { default?: number | null | undefined } = {}) {
	return {
		parse(value: string | null) {
			if (value == null) return defaultValue
			let parsed = Number(value)
			if (Number.isNaN(parsed)) {
				throw new Error(`Expected attribute to be a number, got "${value}"`)
			}
			return value == null ? defaultValue : Number(value)
		},
		serialize(value: number | null) {
			return value == null ? null : String(value)
		},
	}
}

export function string(options: {
	default: string
}): AttributeSerializer<unknown, string, string>
export function string(options?: {
	default?: undefined | null
}): AttributeSerializer<unknown, string | null, string | null>
export function string({
	default: defaultValue = null,
}: { default?: string | null | undefined } = {}) {
	return {
		parse(value: string | null) {
			return value ?? defaultValue
		},
		serialize(value: string | null) {
			return value ?? null
		},
	}
}

export function boolean(
	_options: Record<PropertyKey, never> = {},
): AttributeSerializer<unknown, boolean, boolean> {
	return {
		parse(value: string | null) {
			return value != null
		},
		serialize(value: boolean) {
			return value ? "" : null
		},
	}
}

export function pickList<const K extends string>(options: {
	default?: undefined
	values: Readonly<Array<K>>
}): AttributeSerializer<unknown, K | null, K | null>
export function pickList<const K extends string>(options: {
	default: NoInfer<K>
	values: Readonly<Array<K>>
}): AttributeSerializer<unknown, K, K>
export function pickList<const K extends string>(options: {
	default?: K
	values: Readonly<Array<K>>
}): AttributeSerializer<unknown, K | null, K | null> {
	return {
		parse(value) {
			if (value == null) return options.default ?? null
			if (!options.values.includes(value as K)) {
				throw new Error(
					`Expected attribute to be one of ${options.values.join(
						", ",
					)}, got "${value}"`,
				)
			}
			return value as K
		},
		serialize(value) {
			return value
		},
	}
}
